import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { runStorageMigration } from "@/config/storageVersion";

// Run BEFORE React mounts so cleared keys don't get re-read by components.
runStorageMigration();

// iter413c-24 — Auto-recover from "Loading chunk X failed" by hard-reloading.
// Happens when nginx ships new index.html but the browser still has the old
// one cached and tries to fetch chunks whose hashes have changed. After the
// nginx no-cache header is in place this should be rare, but we keep a
// belt-and-suspenders safety net: one retry per chunk, then full reload.
if (typeof window !== "undefined") {
  const RELOAD_KEY = "__chunk_reload_attempts__";
  window.addEventListener("error", (e) => {
    const msg = String(e?.message || e?.error?.message || "");
    if (msg.includes("Loading chunk") || msg.includes("Loading CSS chunk") || msg.includes("ChunkLoadError")) {
      const attempts = parseInt(sessionStorage.getItem(RELOAD_KEY) || "0", 10);
      if (attempts >= 2) return; // give up after 2 reloads (likely backend issue)
      sessionStorage.setItem(RELOAD_KEY, String(attempts + 1));
      console.warn("[chunk-recover] Reloading due to chunk error:", msg);
      window.location.reload();
    }
  });
  // Clear retry counter on successful full load
  window.addEventListener("load", () => sessionStorage.removeItem(RELOAD_KEY));
}

// Suppress cosmetic hydration mismatch warnings caused by Emergent platform's
// runtime injection of `data-ve-dynamic` / `x-ve-dynamic` span tags.
// These are added AFTER React's initial render by the preview iframe runtime
// and only affect the dev preview — they do NOT impact production users
// (verified Iter 225: no preview-only attributes in deployed HTML, app uses
// createRoot/CSR not hydrateRoot, so no real hydration ever happens).
if (typeof window !== "undefined") {
  const origError = console.error;
  console.error = function (...args) {
    const msg = args[0];
    if (typeof msg === "string" && (
      msg.includes("Hydration failed") ||
      msg.includes("data-ve-dynamic") ||
      msg.includes("x-ve-dynamic") ||
      msg.includes("did not match. Server") ||
      msg.includes("There was an error while hydrating") ||
      msg.includes("Text content does not match server-rendered HTML") ||
      // React 18+ DOM nesting warnings caused by the same span-injection runtime:
      msg.includes("In HTML, <span> cannot be a child of <option>") ||
      msg.includes("validateDOMNesting") ||
      msg.includes("cannot appear as a child of")
    )) {
      return; // silently drop — preview-only noise
    }
    return origError.apply(console, args);
  };
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
